import { animate, style, transition, trigger } from '@angular/animations';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Contact } from '@components/form/components/form-layout-3/model';
import { ContactUsService } from '@components/form/components/form-layout-3/services';
import { ElasticSearchService } from '@services/elastic-search';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { FormItemComponent } from '@shared/components/form-item';
import { FormAbstractComponent } from '@shared/components/form-item/abstract';
import { FormItem } from '@shared/components/form-item/model';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { CustomError } from '@shared/models';
import { catchError, EMPTY, of, take } from 'rxjs';

@Component({
  selector: 'app-form-layout-4',
  templateUrl: './form-layout-4.component.html',
  styleUrl: './form-layout-4.component.scss',
  imports: [TitleComponent, FormItemComponent, ButtonComponent],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLayout4Component extends FormAbstractComponent {
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly #elasticSearchService = inject(ElasticSearchService);
  readonly #contactUsService = inject(ContactUsService);
  readonly #logsService = inject(LogsService);

  readonly isSending = signal(false);
  readonly isSent = signal(false);
  readonly isSendFailed = signal(false);
  readonly confirmation = viewChild<ElementRef<HTMLElement>>('confirmation');

  // H2: the landing page hero already carries the page H1
  readonly title: Title = {
    level: 'H2',
    text: 'Parlons de votre projet, de vos envies.',
    color: 'BLACK',
    align: {
      mobile: 'LEFT',
      desktop: 'LEFT',
    },
    size: {
      mobile: 'H1',
      desktop: 'H1',
    },
  };

  readonly specs = {
    fullName: {
      type: 'TEXT',
      key: 'fullName',
      placeholder: 'saisir vos prénom et nom',
      autocomplete: 'off',
      rules: {
        regexp: /^([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+\s+([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+$/,
        tips: 'Une information est manquante',
      },
    },
    offer: {
      type: 'SEARCH',
      key: 'offer',
      placeholder: 'saisir une offre',
      list: [
        {
          key: 'CONSEIL',
          label: 'Conseil décoration',
        },
        {
          key: 'PROJET',
          label: 'Projet décoration',
        },
      ],
    },
    message: {
      type: 'TEXTAREA',
      key: 'message',
    },
    phone: {
      type: 'TEXT',
      key: 'phone',
      autocomplete: 'off',
      placeholder: 'saisir votre numéro de téléphone',
      rules: {
        regexp: /^0[1-9]\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/,
        tips: 'Numéro de téléphone invalide',
      },
    },
    email: {
      type: 'TEXT',
      key: 'email',
      autocomplete: 'off',
      placeholder: 'saisir votre adresse email',
      rules: {
        regexp: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        tips: 'Adresse email invalide',
      },
    },
    optin: {
      type: 'BOOLEAN',
      key: 'optin',
      label:
        "Je souhaite m'abonner à la newsletter Perene et recevoir chaque trimestre, de l'inspiration dans ma boîte mail.",
    },
  } satisfies Record<string, FormItem>;

  readonly stores = toSignal(
    this.#elasticSearchService.getStores({ decoConsultingEnabled: true }).pipe(
      catchError((err: unknown) => {
        this.#logsService.error('FormLayout4Component', 'Eligible stores search failed', err);
        return of([]);
      })
    ),
    { initialValue: [] }
  );

  readonly storeSpecs = computed<FormItem>(() => ({
    type: 'SEARCH',
    key: 'store',
    placeholder: 'choisir une boutique',
    list: this.stores().map((store) => ({ key: store.uuid, label: store.name })),
  }));

  constructor() {
    super();
    // the submit button disappears with the form, so focus moves to the message replacing it
    afterRenderEffect(() => this.confirmation()?.nativeElement.focus());
  }

  isStepVisible(requiredFields: string[]): boolean {
    return requiredFields.every((field) => !!this.form[field]);
  }

  onSubmit(): void {
    if (this.isSending() || !this.isValidForm()) return;
    this.isSending.set(true);
    this.isSendFailed.set(false);
    const form = this.form as Record<string, unknown>;
    const fullNameParts: string[] = (form['fullName'] as string).split(' ');
    const offer = form['offer'] as { key: string; label: string };
    const store = form['store'] as { key: string; label: string };

    const toSend: Contact = {
      firstname: fullNameParts[0]!,
      lastname: fullNameParts.slice(1).join(' '),
      phone: (form['phone'] as string).replace(/\s+/g, ''),
      email: form['email'] as string,
      message: form['message'] as string,
      type: '/contact_types/consulting_deco',
      // the label, not the key: the API writes ssType as is into the email sent to the store
      ssType: offer.label,
      store: `/stores/${store.key}`,
      optin: !!form['optin'],
    };

    this.#contactUsService
      .create(toSend, true)
      .pipe(
        take(1),
        catchError((err: unknown) => {
          // Never log toSend (visitor PII); skip err.error too (validation bodies may echo fields).
          const { status, server } = err as Partial<CustomError>;
          this.#logsService.error('FormLayout4Component', 'Error while saving contact', {
            status,
            url: server?.url,
            type: toSend.type,
            ssType: toSend.ssType,
            store: toSend.store,
          });
          this.isSending.set(false);
          this.isSendFailed.set(true);
          return EMPTY;
        })
      )
      .subscribe(() => this.isSent.set(true));
  }
}
